import React from 'react';
import { Modal } from 'src/components/Modal/Modal';
import { ModalHeader } from 'src/components/Modal/ModalHeader';
import { ModalBody } from 'src/components/Modal/ModalBody';
import { ModalFooter } from 'src/components/Modal/ModalFooter';
import { ButtonLink } from 'src/components/Button/ButtonLink';
import { Price } from 'src/components/Helpers/Price';
import { artWorkEntryUrl } from 'src/components/CustomisationPage/utils/customisationsUrls';
import { EntryKind } from 'src/components/CustomisationPage/feature/customisation.actions';
import { ProductSummaryChild } from 'src/components/Basket/utils/basket-customisations';
import { ProductPrice } from 'src/types/global-types';
import { useVatCausesRender } from 'src/components/AppShell/VatProvider';
import { AtbModalSkeleton } from 'src/elements/Skeletons/Skeletons';

import { ProductBlock } from './ProductBlock';
import classes from './AtbModal.scss';

export type AtbDetails = {
    /**
     * general info for read-only output in the modal
     */
    name: string;
    image: string;
    sku: string;
    /**
     * Overall qty
     */
    qty: number;
    /**
     * The current price of each item
     */
    itemPrice: number;
    /**
     * The line-items
     */
    items: ProductSummaryChild[];
    /**
     * Use this to show a total price in place of per-item
     */
    totalPrice?: ProductPrice;
};

type Props = {
    close: (activate?: boolean) => void;
    detail: AtbDetails;
    itemIds: number[];
    showItemPrice?: boolean;
    showArtworkLink?: boolean;
    pending?: boolean;
    noQuantity?: boolean;
};

export const AtbModal: React.FC<Props> = (props) => {
    return (
        <Modal type="popup" isOpen={true} onDismiss={props.close}>
            <ModalHeader
                title="Added to basket"
                icon="check-circle"
                type="success"
                onDismiss={props.close}
                controlScroll
            />
            <ModalBody>{props.pending ? <AtbModalSkeleton close={props.close} /> : <Atb {...props} />}</ModalBody>
            <ModalFooter onDismiss={props.close} cancelText="Continue Shopping" controlScroll />
        </Modal>
    );
};

export const Atb: React.FC<Props> = ({ itemIds, detail, showItemPrice, showArtworkLink, noQuantity }) => {
    const { vat } = useVatCausesRender();
    const { sku, items, itemPrice, image, name, qty, totalPrice } = detail;
    const artWorkUrl = artWorkEntryUrl({ itemIds, kind: EntryKind.New });
    const price: ProductPrice = totalPrice || {
        regular_price_incl: 0,
        regular_price: 0,
        final_price_incl: 0,
        final_price: 0,
        price: qty * itemPrice,
        price_incl: qty * itemPrice,
    };
    return (
        <div className={classes.atb} data-show-item-price={String(showItemPrice)}>
            <ProductBlock product={{ image, name, items, sku }} variant="large" noQuantity={noQuantity} />
            <div className={classes.atbTotals}>
                <p className={classes.atbTotalsQty} data-test-id="Atb_item_count">
                    {qty} items
                </p>
                <p className={classes.atbTotalsPrice}>
                    <Price price={itemPrice} priceIncl={itemPrice} /> <small>per item</small>
                </p>
                <p className={classes.atbTotalsPriceDupe} data-test-id="Atb_item_count">
                    {qty} items
                </p>
                <p className={classes.atbTotalsGrand} data-test-id="Atb_total">
                    <small>Total:</small> <Price price={price.price} priceIncl={price.price_incl} vat={vat} />{' '}
                    <small>{vat ? 'inc' : 'ex'}. VAT</small>
                </p>
            </div>
            <div className={classes.atbActions}>
                {showArtworkLink && <ButtonLink to={artWorkUrl}>Add your logo now</ButtonLink>}
                {!showArtworkLink && <span> </span>}
                <ButtonLink variant={showArtworkLink ? 'light' : 'primary'} to="/basket">
                    View Basket
                </ButtonLink>
            </div>
        </div>
    );
};
